/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
// Interval Reference System - Song examples for ear training
// Using generic descriptions to avoid copyright issues

export interface IntervalReference {
  interval: string;
  direction: 'ascending' | 'descending';
  examples: {
    description: string;
    category: 'movie' | 'classical' | 'folk' | 'pop' | 'effect' | 'traditional';
    commonName: string;
    memoryTip?: string;
  }[];
}

export const intervalReferences: IntervalReference[] = [
  {
    interval: 'P1',
    direction: 'ascending',
    examples: [
      {
        description: 'Same note played twice',
        category: 'effect',
        commonName: 'Unison',
        memoryTip: 'Perfect match - no difference in pitch'
      }
    ]
  },
  {
    interval: 'm2',
    direction: 'ascending',
    examples: [
      {
        description: 'Ominous shark movie theme',
        category: 'movie',
        commonName: 'Jaws',
        memoryTip: 'Tense, scary, close together notes'
      },
      {
        description: 'Funeral march progression',
        category: 'classical',
        commonName: 'Chopin Funeral March',
        memoryTip: 'Dark and somber feeling'
      },
      {
        description: 'Chromatic step up',
        category: 'effect',
        commonName: 'Half-step',
        memoryTip: 'Smallest possible step in Western music'
      }
    ]
  },
  {
    interval: 'M2',
    direction: 'ascending',
    examples: [
      {
        description: 'Happy Birthday opening',
        category: 'traditional',
        commonName: 'Happy Birthday',
        memoryTip: 'Gentle step up, familiar and warm'
      },
      {
        description: 'Do-Re from Sound of Music',
        category: 'pop',
        commonName: 'Do-Re-Mi',
        memoryTip: 'First step of major scale'
      },
      {
        description: 'Frère Jacques opening',
        category: 'folk',
        commonName: 'Brother John',
        memoryTip: 'Simple, childlike melody'
      }
    ]
  },
  {
    interval: 'm3',
    direction: 'ascending',
    examples: [
      {
        description: 'Greensleeves opening melody',
        category: 'traditional',
        commonName: 'Greensleeves',
        memoryTip: 'Sad, minor feeling'
      },
      {
        description: 'Smoke on the Water main riff',
        category: 'pop',
        commonName: 'Rock classic riff',
        memoryTip: 'Heavy, bluesy sound'
      },
      {
        description: 'Brahms Lullaby',
        category: 'classical',
        commonName: 'Lullaby',
        memoryTip: 'Gentle, soothing minor leap'
      }
    ]
  },
  {
    interval: 'M3',
    direction: 'ascending',
    examples: [
      {
        description: 'When the Saints Go Marching In',
        category: 'traditional',
        commonName: 'Saints',
        memoryTip: 'Bright, happy, triumphant'
      },
      {
        description: 'Kumbaya opening',
        category: 'folk',
        commonName: 'Kumbaya',
        memoryTip: 'Warm, major chord feeling'
      },
      {
        description: 'Morning Has Broken',
        category: 'traditional',
        commonName: 'Morning hymn',
        memoryTip: 'Uplifting, bright start'
      }
    ]
  },
  {
    interval: 'P4',
    direction: 'ascending',
    examples: [
      {
        description: 'Wedding march opening',
        category: 'classical',
        commonName: 'Here Comes the Bride',
        memoryTip: 'Ceremonial, stable, strong'
      },
      {
        description: 'Amazing Grace opening',
        category: 'traditional',
        commonName: 'Amazing Grace',
        memoryTip: 'Spiritual, reaching upward'
      },
      {
        description: 'Auld Lang Syne opening',
        category: 'traditional',
        commonName: 'New Year\'s song',
        memoryTip: 'Nostalgic, reflective leap'
      }
    ]
  },
  {
    interval: 'TRI',
    direction: 'ascending',
    examples: [
      {
        description: 'Simpsons theme opening',
        category: 'pop',
        commonName: 'TV cartoon theme',
        memoryTip: 'Unusual, unstable, quirky'
      },
      {
        description: 'Maria from West Side Story',
        category: 'pop',
        commonName: 'Broadway classic',
        memoryTip: 'Yearning, unresolved tension'
      }
    ]
  },
  {
    interval: 'P5',
    direction: 'ascending',
    examples: [
      {
        description: 'Epic space movie theme',
        category: 'movie',
        commonName: 'Star Wars',
        memoryTip: 'Heroic, wide, powerful leap'
      },
      {
        description: 'Twinkle Twinkle Little Star',
        category: 'folk',
        commonName: 'Twinkle Twinkle',
        memoryTip: 'Perfect, stable, complete sound'
      },
      {
        description: 'Superman theme opening',
        category: 'movie',
        commonName: 'Superhero theme',
        memoryTip: 'Soaring, triumphant'
      }
    ]
  },
  {
    interval: 'm6',
    direction: 'ascending',
    examples: [
      {
        description: 'Black Orpheus theme',
        category: 'pop',
        commonName: 'Bossa nova classic',
        memoryTip: 'Melancholy, longing'
      },
      {
        description: 'Love Story theme',
        category: 'movie',
        commonName: 'Romance movie theme',
        memoryTip: 'Romantic, yearning'
      }
    ]
  },
  {
    interval: 'M6',
    direction: 'ascending',
    examples: [
      {
        description: 'My Bonnie Lies Over the Ocean',
        category: 'folk',
        commonName: 'My Bonnie',
        memoryTip: 'Wide, hopeful leap'
      },
      {
        description: 'NBC chimes (first two notes)',
        category: 'effect',
        commonName: 'TV network jingle',
        memoryTip: 'Bright, clear, recognizable'
      }
    ]
  },
  {
    interval: 'm7',
    direction: 'ascending',
    examples: [
      {
        description: 'Watermelon Man opening',
        category: 'pop',
        commonName: 'Jazz standard',
        memoryTip: 'Bluesy, unresolved tension'
      },
      {
        description: 'Somewhere from West Side Story',
        category: 'pop',
        commonName: 'Broadway ballad',
        memoryTip: 'Dreamy, floating feeling'
      }
    ]
  },
  {
    interval: 'M7',
    direction: 'ascending',
    examples: [
      {
        description: 'Take On Me chorus leap',
        category: 'pop',
        commonName: '80s pop hit',
        memoryTip: 'Dramatic, almost octave'
      },
      {
        description: 'Don\'t Know Why opening',
        category: 'pop',
        commonName: 'Jazz ballad',
        memoryTip: 'Sophisticated, jazzy'
      }
    ]
  },
  {
    interval: 'P8',
    direction: 'ascending',
    examples: [
      {
        description: 'Somewhere Over the Rainbow opening',
        category: 'movie',
        commonName: 'Wizard of Oz',
        memoryTip: 'Perfect octave - same note higher'
      },
      {
        description: 'Singing in the Rain',
        category: 'movie',
        commonName: 'Movie musical',
        memoryTip: 'Complete, full circle feeling'
      }
    ]
  }
];

// Helper function to get references for a specific interval
export const getIntervalReferences = (interval: string): IntervalReference | undefined => {
  return intervalReferences.find(ref => ref.interval === interval);
};

// Helper function to get a random reference for an interval
export const getRandomReference = (interval: string): IntervalReference['examples'][0] | undefined => {
  const references = getIntervalReferences(interval);
  if (!references || references.examples.length === 0) return undefined;
  
  const randomIndex = Math.floor(Math.random() * references.examples.length);
  return references.examples[randomIndex];
};